import toast from 'react-hot-toast';

// API base URL - uses same-domain routing via Worker route
// Local dev uses Vite proxy, production uses Worker route on etsyflow.michalkurecka.cz/api/*
const API_BASE = '/api';

// Shared types
export type Model = {
  id: string;
  name: string;
  provider: string;
  type: 'text' | 'image';
  is_active: boolean;
  context_length?: number;
  pricing?: {
    prompt: string;
    completion: string;
    image?: string;
  };
};

export type Niche = {
  id: number;
  keyword: string;
  trend_score: number;
  status: string;
  source?: string;
  product_type?: PODProductType;
  recipe_id?: number | null;
  created_at: string;
};

export type Listing = {
  id: number;
  niche_id?: number;
  title: string;
  description: string;
  tags: string;
  price?: number;
  status: string;
  etsy_listing_id?: string;
  etsy_category_id?: number;
  created_at?: string;
  updated_at?: string;
  niche_keyword?: string;
  primary_image?: string;
  // Printify POD fields
  product_type?: 'digital_download' | 'printify_pod';
  printify_product_id?: string;
  printify_blueprint_id?: number;
  printify_provider_id?: number;
  printify_image_id?: string;
  base_price?: number;
  concept?: string;
};

export type PrintifyBlueprint = {
  id: number;
  title: string;
  description: string;
  brand: string;
  images: string[];
};

export type PrintifyProvider = {
  id: number;
  title: string;
  location?: {
    country: string;
  };
};

export type PrintifyConfig = {
  id: number;
  name: string;
  blueprint_id: number;
  print_provider_id: number;
  product_type: string;
  product_model?: string;
  default_price: number;
  is_default: number;
  metadata?: string;
  created_at: string;
};

export type Recipe = {
  id: string;
  name: string;
  description: string | null;
  image_model_id: string | null;
  text_model_id: string | null;
  base_prompt_template: string;
  negative_prompt: string | null;
  aspect_ratio: string;
  created_at: string;
  updated_at: string;
};

export type EtsyCategory = {
  id: number;
  level: number;
  name: string;
  parent_id: number | null;
  children?: EtsyCategory[];
  full_path_taxonomy_ids?: number[];
};

export type PODProductType =
  | 't-shirt'
  | 'hoodie'
  | 'sweatshirt'
  | 'tank-top'
  | 'mug'
  | 'poster'
  | 'canvas'
  | 'phone-case'
  | 'tote-bag'
  | 'sticker'
  | 'all';

export type Asset = {
  id: number;
  listing_id: number;
  r2_url: string;
  r2_key: string;
  asset_type: string;
  is_current?: number;
  created_at?: string;
};

export type ContentHistoryItem = {
  id: number;
  listing_id: number;
  title: string | null;
  description: string | null;
  tags: string | null;
  text_model_id: string | null;
  recipe_id: number | null;
  recipe_name: string | null;
  source: 'initial' | 'regenerate' | 'restore' | 'manual';
  created_at: string;
};

export type ImageHistoryItem = {
  id: number;
  r2_url: string;
  asset_type: string;
  is_current: number;
  created_at: string;
};

export type ProcessingJobStatus =
  | 'queued'
  | 'processing'
  | 'upscaling'
  | 'mockup_gen'
  | 'complete'
  | 'failed';
export type ProcessingJobType = 'edge_press' | 'mockup' | 'upscale' | 'image_gen';

export type ProcessingJob = {
  id: number;
  listing_id: number;
  job_type: ProcessingJobType;
  status: ProcessingJobStatus;
  progress: number;
  last_error: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

// Batch Generation Types
export type BatchStrategy =
  | 'different_concepts_styles'
  | 'same_concept_different_styles'
  | 'different_concepts_same_style'
  | 'matrix';

export type ProductStyle =
  | 'minimalist'
  | 'watercolor'
  | 'vintage'
  | 'boho'
  | 'modern'
  | 'whimsical'
  | 'botanical'
  | 'geometric'
  | 'hand-drawn'
  | 'abstract';

export type AnalysisInfluence = {
  targetAudience: number;
  opportunities: number;
  demandSignals: number;
  priceRange: number;
};

export type BatchGeneration = {
  id: string;
  niche_id: number;
  niche_keyword: string;
  strategy: BatchStrategy;
  product_type: PODProductType;
  variant_count: number;
  variants_completed: number;
  variants_selected: number;
  variants_converted: number;
  status: 'pending' | 'generating' | 'ready' | 'partial' | 'failed';
  total_cost_usd: number;
  created_at: string;
  completed_at: string | null;
};

export type BatchVariant = {
  id: string;
  concept_name: string;
  concept_description: string;
  style: ProductStyle;
  product_type: string;
  preview_image_url: string | null;
  status: 'pending' | 'generating' | 'ready' | 'selected' | 'converted' | 'rejected' | 'failed';
  listing_id: number | null;
  total_cost_usd: number;
  niche_analysis_influence: {
    targetAudience?: string;
    opportunity?: string;
    demandSignal?: string;
    priceLevel?: string;
  } | null;
  created_at: string;
};

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const message = error.details
      ? `${error.error || 'Request failed'}: ${error.details}`
      : error.error || 'Request failed';

    // Show toast for errors (except on initial page loads)
    if (options?.method && options.method !== 'GET') {
      toast.error(message);
    }

    throw new Error(message);
  }

  return response.json();
}

export const api = {
  // Models
  models: {
    list: () => fetchApi<{ models: Model[] }>('/models'),
    get: (id: string) => fetchApi<{ model: Model }>(`/models/${id}`),
    create: (data: { id: string; provider: string; type: 'text' | 'image' }) =>
      fetchApi('/models', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<{ provider: string; type: string; is_active: boolean }>) =>
      fetchApi(`/models/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  // Niches
  niches: {
    list: (status?: string) =>
      fetchApi<{ niches: (Niche & { analysis?: string })[] }>(
        status ? `/niches?status=${status}` : '/niches'
      ),
    get: (id: number) => fetchApi<{ niche: Niche & { analysis?: string } }>(`/niches/${id}`),
    create: (data: {
      keyword: string;
      trend_score?: number;
      status?: string;
      product_type?: PODProductType;
      recipe_id?: number;
    }) => fetchApi('/niches', { method: 'POST', body: JSON.stringify(data) }),
    update: (
      id: number,
      data: Partial<{
        keyword: string;
        trend_score: number;
        status: string;
        product_type: PODProductType;
        recipe_id: number | null;
      }>
    ) => fetchApi(`/niches/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => fetchApi(`/niches/${id}`, { method: 'DELETE' }),
    reviewQueue: (limit?: number) =>
      fetchApi<{ niches: (Niche & { analysis?: string })[] }>(
        `/niches/review-queue${limit ? `?limit=${limit}` : ''}`
      ),
  },

  // Research
  research: {
    analyze: (nicheId: number, model?: string) =>
      fetchApi<{
        success: boolean;
        nicheId: number;
        keyword: string;
        trendScore: number;
        analysis: unknown;
      }>(`/research/analyze/${nicheId}`, {
        method: 'POST',
        body: JSON.stringify({ model }),
      }),
    trending: (minScore?: number, limit?: number) => {
      const params = new URLSearchParams();
      if (minScore) params.set('minScore', String(minScore));
      if (limit) params.set('limit', String(limit));
      const qs = params.toString();
      return fetchApi<{ niches: Niche[] }>(qs ? `/research/trending?${qs}` : '/research/trending');
    },
  },

  // Listings
  listings: {
    list: (params?: { status?: string; niche_id?: number }) => {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.niche_id) query.set('niche_id', String(params.niche_id));
      const qs = query.toString();
      return fetchApi<{ listings: Listing[] }>(qs ? `/listings?${qs}` : '/listings');
    },
    get: (id: string) => fetchApi<{ listing: Listing & { assets?: Asset[] } }>(`/listings/${id}`),
    create: (data: Partial<Listing>) =>
      fetchApi('/listings', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: Partial<Listing>) =>
      fetchApi(`/listings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => fetchApi(`/listings/${id}`, { method: 'DELETE' }),
    contentHistory: (id: string) =>
      fetchApi<{ history: ContentHistoryItem[] }>(`/listings/${id}/content-history`),
    imageHistory: (id: string) =>
      fetchApi<{ images: ImageHistoryItem[] }>(`/listings/${id}/image-history`),
    restoreContent: (id: string, historyId: number) =>
      fetchApi<{ success: boolean }>(`/listings/${id}/restore-content`, {
        method: 'POST',
        body: JSON.stringify({ history_id: historyId }),
      }),
    setCurrentImage: (id: string, assetId: number) =>
      fetchApi<{ success: boolean }>(`/listings/${id}/set-current-image`, {
        method: 'POST',
        body: JSON.stringify({ asset_id: assetId }),
      }),
    clone: (id: string, data: { printify_config_id?: number; regenerate_seo?: boolean }) =>
      fetchApi<{
        success: boolean;
        listing_id: number;
        product_type: string;
        message: string;
      }>(`/listings/${id}/clone`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Assets
  assets: {
    list: (listingId?: number) =>
      fetchApi<{ assets: Asset[] }>(listingId ? `/assets?listing_id=${listingId}` : '/assets'),
    upload: (file: File, listingId: number, assetType: string) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('listing_id', String(listingId));
      formData.append('asset_type', assetType);
      return fetch(`${API_BASE}/assets/upload`, {
        method: 'POST',
        body: formData,
      }).then((r) => r.json());
    },
    delete: (id: number) => fetchApi(`/assets/${id}`, { method: 'DELETE' }),
  },

  // Etsy
  etsy: {
    categories: () =>
      fetchApi<{ success: boolean; categories: EtsyCategory[]; count: number }>('/etsy/categories'),
    sync: (listingId: number, data?: { price?: number; quantity?: number; taxonomy_id?: number }) =>
      fetchApi<{
        success: boolean;
        etsy_listing_id: number;
        etsy_url: string;
        state: string;
        sync_status: 'published' | 'sync_failed';
        image_upload_warning?: string | null;
      }>(`/etsy/sync/${listingId}`, { method: 'POST', body: JSON.stringify(data ?? {}) }),
    status: () =>
      fetchApi<{
        connected: boolean;
        shop_name?: string;
        shop_id?: number;
        currency?: string;
        active_listings?: number;
        message?: string;
        error?: string;
      }>('/etsy/status'),
  },

  // Pipeline
  pipeline: {
    brainstorm: (data: { niche_id: number; keyword?: string; count?: number }) =>
      fetchApi('/pipeline/brainstorm', { method: 'POST', body: JSON.stringify(data) }),
    design: (data: {
      listing_id: number;
      concept: string;
      style?: string;
      product_type?: 'wall_art' | 'apparel';
    }) => fetchApi('/pipeline/design', { method: 'POST', body: JSON.stringify(data) }),
    seo: (data: {
      listing_id: number;
      concept: string;
      niche_keyword?: string;
      recipe_id?: string;
    }) =>
      fetchApi<{
        success: boolean;
        title: string;
        tags: string[];
        description: string;
        warnings?: string[];
        usage: { prompt_tokens: number; completion_tokens: number; cost: number };
      }>('/pipeline/seo', { method: 'POST', body: JSON.stringify(data) }),
    mockup: (data: { listing_id: number; asset_id: number; templates?: string[] }) =>
      fetchApi('/pipeline/mockup', { method: 'POST', body: JSON.stringify(data) }),
    stage: (data: { listing_id: number }) =>
      fetchApi('/pipeline/stage', { method: 'POST', body: JSON.stringify(data) }),
    generate: (data: {
      niche_id: number;
      text_model?: string;
      image_model?: string;
      style?: string;
      recipe_id?: string;
      product_type?: PODProductType;
    }) =>
      fetchApi<{
        success: boolean;
        listing_id: number;
        concept: { name: string; description: string };
        seo: { title: string; tags: string[]; description: string };
        asset: { id: number; url: string };
        total_cost: number;
      }>('/pipeline/generate', { method: 'POST', body: JSON.stringify(data) }),
    trademarkCheck: (text: string) =>
      fetchApi<{
        isClean: boolean;
        violations: Array<{ term: string; category: string; position: number }>;
        report: string;
      }>('/pipeline/trademark/check', { method: 'POST', body: JSON.stringify({ text }) }),
    trademarkStats: () =>
      fetchApi<{
        totalTerms: number;
        categories: Record<string, number>;
      }>('/pipeline/trademark/stats'),
  },

  // Costs
  costs: {
    summary: () =>
      fetchApi<{
        today: { total: number; count: number };
        week: { total: number; count: number };
        month: { total: number; count: number };
        all_time: { total: number; count: number };
        by_operation: Record<string, { total: number; count: number }>;
        by_model: Record<string, { total: number; count: number }>;
        budget: {
          daily_limit: number;
          weekly_limit: number;
          monthly_limit: number;
          daily_remaining: number;
          weekly_remaining: number;
          monthly_remaining: number;
          alerts: string[];
        };
      }>('/costs/summary'),
    perListing: (params?: { limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));
      const qs = query.toString();
      return fetchApi<{
        listings: Array<{
          listing_id: number;
          title: string;
          status: string;
          total_cost: number;
          operation_count: number;
          operations: string[];
          models: string[];
          last_activity: string;
        }>;
        pagination: { total: number; limit: number; offset: number; hasMore: boolean };
        stats: { average_cost_per_listing: number; total_listings: number };
      }>(qs ? `/costs/per-listing?${qs}` : '/costs/per-listing');
    },
    chartData: (days?: number) =>
      fetchApi<{
        daily_totals: Array<{ date: string; total: number; count: number }>;
        by_operation: Array<{ date: string; operation_type: string; total: number }>;
        period_days: number;
      }>(days ? `/costs/chart-data?days=${days}` : '/costs/chart-data'),
    history: (params?: { limit?: number; offset?: number; listing_id?: number }) => {
      const query = new URLSearchParams();
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));
      if (params?.listing_id) query.set('listing_id', String(params.listing_id));
      const qs = query.toString();
      return fetchApi<{
        costs: Array<{
          id: number;
          listing_id: number | null;
          operation_type: string;
          model: string;
          prompt_tokens: number;
          completion_tokens: number;
          cost_usd: number;
          created_at: string;
        }>;
        pagination: { limit: number; offset: number; returned: number };
      }>(qs ? `/costs/history?${qs}` : '/costs/history');
    },
  },

  // Printify POD
  printify: {
    // Catalog
    blueprints: () => fetchApi<{ blueprints: PrintifyBlueprint[] }>('/printify/catalog/blueprints'),
    wallArtBlueprints: () =>
      fetchApi<{ blueprints: PrintifyBlueprint[] }>('/printify/catalog/blueprints/wall-art'),
    apparelBlueprints: () =>
      fetchApi<{ blueprints: PrintifyBlueprint[] }>('/printify/catalog/blueprints/apparel'),
    drinkwareBlueprints: () =>
      fetchApi<{ blueprints: PrintifyBlueprint[] }>('/printify/catalog/blueprints/drinkware'),
    providers: (blueprintId: number) =>
      fetchApi<{ providers: PrintifyProvider[] }>(
        `/printify/catalog/blueprints/${blueprintId}/providers`
      ),

    // Config presets
    configs: () => fetchApi<{ configs: PrintifyConfig[] }>('/printify/config'),
    saveConfig: (data: {
      name: string;
      blueprint_id: number;
      print_provider_id: number;
      product_type: string;
      product_model?: string;
      default_price?: number;
      is_default?: boolean;
    }) =>
      fetchApi<{ success: boolean; id: number }>('/printify/config', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    deleteConfig: (id: number) =>
      fetchApi<{ success: boolean }>(`/printify/config/${id}`, { method: 'DELETE' }),

    // Pipeline
    createFromListing: (data: {
      listing_id: number;
      blueprint_id: number;
      print_provider_id: number;
      price?: number;
    }) =>
      fetchApi<{
        success: boolean;
        listing_id: number;
        printify_product_id: string;
        printify_image_id: string;
        message: string;
      }>('/printify/pipeline/create-from-listing', { method: 'POST', body: JSON.stringify(data) }),

    publishListing: (listingId: number) =>
      fetchApi<{ success: boolean; listing_id: number; message: string }>(
        '/printify/pipeline/publish-listing',
        { method: 'POST', body: JSON.stringify({ listing_id: listingId }) }
      ),

    // Products
    products: () =>
      fetchApi<{
        products: Array<{
          id: string;
          title: string;
          description: string;
          visible: boolean;
          created_at: string;
        }>;
      }>('/printify/products'),
  },

  // Recipes
  recipes: {
    list: () => fetchApi<{ recipes: Recipe[] }>('/recipes'),
    get: (id: string) => fetchApi<{ recipe: Recipe }>(`/recipes/${id}`),
    create: (data: {
      name: string;
      description?: string;
      image_model_id?: string;
      text_model_id?: string;
      base_prompt_template: string;
      negative_prompt?: string;
      aspect_ratio?: string;
    }) =>
      fetchApi<{ success: boolean; recipe: Recipe }>('/recipes', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (
      id: string,
      data: {
        name?: string;
        description?: string | null;
        image_model_id?: string | null;
        text_model_id?: string | null;
        base_prompt_template?: string;
        negative_prompt?: string | null;
        aspect_ratio?: string;
      }
    ) =>
      fetchApi<{ success: boolean; recipe: Recipe }>(`/recipes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) => fetchApi<{ success: boolean }>(`/recipes/${id}`, { method: 'DELETE' }),
  },

  // Processing Jobs
  processingJobs: {
    list: (params?: {
      listing_id?: number;
      status?: ProcessingJobStatus;
      job_type?: ProcessingJobType;
    }) => {
      const query = new URLSearchParams();
      if (params?.listing_id) query.set('listing_id', String(params.listing_id));
      if (params?.status) query.set('status', params.status);
      if (params?.job_type) query.set('job_type', params.job_type);
      const qs = query.toString();
      return fetchApi<{ jobs: ProcessingJob[] }>(
        qs ? `/processing-jobs?${qs}` : '/processing-jobs'
      );
    },
    get: (id: number) => fetchApi<{ job: ProcessingJob }>(`/processing-jobs/${id}`),
    create: (data: {
      listing_id: number;
      job_type: ProcessingJobType;
      status?: ProcessingJobStatus;
      progress?: number;
      metadata?: Record<string, unknown>;
    }) =>
      fetchApi<{ success: boolean; id: number }>('/processing-jobs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (
      id: number,
      data: {
        status?: ProcessingJobStatus;
        progress?: number;
        last_error?: string | null;
        metadata?: Record<string, unknown>;
      }
    ) =>
      fetchApi<{ success: boolean; job: ProcessingJob }>(`/processing-jobs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      fetchApi<{ success: boolean }>(`/processing-jobs/${id}`, { method: 'DELETE' }),
  },

  // Batch Generation
  batch: {
    // List all batches
    list: (params?: { niche_id?: number; status?: string; limit?: number }) => {
      const query = new URLSearchParams();
      if (params?.niche_id) query.set('niche_id', String(params.niche_id));
      if (params?.status) query.set('status', params.status);
      if (params?.limit) query.set('limit', String(params.limit));
      const qs = query.toString();
      return fetchApi<{ batches: BatchGeneration[] }>(qs ? `/batch?${qs}` : '/batch');
    },

    // Get batch with variants
    get: (batchId: string) =>
      fetchApi<{ batch: BatchGeneration; variants: BatchVariant[] }>(`/batch/${batchId}`),

    // Start batch generation
    generate: (data: {
      niche_id: number;
      variant_count: number;
      strategy: BatchStrategy;
      recipe_id?: string;
      product_type?: PODProductType;
      styles?: ProductStyle[];
      concept_count?: number;
      analysis_influence?: Partial<AnalysisInfluence>;
      text_model?: string;
      image_model?: string;
    }) =>
      fetchApi<{
        success: boolean;
        batch_id: string;
        niche_id: number;
        variant_count: number;
        variants_completed: number;
        strategy: string;
        status: string;
        total_cost: number;
      }>('/batch/generate', { method: 'POST', body: JSON.stringify(data) }),

    // Select/deselect variants
    select: (
      batchId: string,
      data: { variant_ids: string[]; action: 'select' | 'deselect' | 'reject' }
    ) =>
      fetchApi<{
        success: boolean;
        updated_count: number;
        batch_status: { variants_selected: number; variants_rejected: number };
      }>(`/batch/${batchId}/select`, { method: 'PUT', body: JSON.stringify(data) }),

    // Convert selected variants to listings
    convert: (
      batchId: string,
      data: {
        variant_ids?: string[];
        generate_seo?: boolean;
        generate_mockups?: boolean;
        auto_stage?: boolean;
      }
    ) =>
      fetchApi<{
        success: boolean;
        converted_count: number;
        listings: Array<{ variant_id: string; listing_id: number; title: string; status: string }>;
        total_cost_usd: number;
      }>(`/batch/${batchId}/convert`, { method: 'POST', body: JSON.stringify(data) }),

    // Delete batch
    delete: (batchId: string) =>
      fetchApi<{ success: boolean }>(`/batch/${batchId}`, { method: 'DELETE' }),

    // Get available styles
    styles: () => fetchApi<{ styles: ProductStyle[] }>('/batch/styles'),

    // Estimate cost
    estimate: (variantCount: number, imageModel?: string) => {
      const query = new URLSearchParams();
      query.set('variant_count', String(variantCount));
      if (imageModel) query.set('image_model', imageModel);
      return fetchApi<{
        variantCount: number;
        textCost: number;
        imageCost: number;
        totalCost: number;
      }>(`/batch/estimate?${query.toString()}`);
    },
  },

  // Analytics
  analytics: {
    dashboard: () =>
      fetchApi<{
        overall: {
          total_listings: number;
          pod_listings: number;
          total_orders: number;
          total_revenue_cents: number;
          listings_with_orders: number;
        };
        top_recipes: Array<{
          recipe_id: number;
          recipe_name: string;
          listing_count: number;
          total_orders: number;
          total_revenue_cents: number;
          avg_orders_per_listing: number;
          avg_revenue_per_listing: number;
        }>;
        top_niches: Array<{
          niche_id: number;
          niche_keyword: string;
          product_type: string;
          recipe_id: number;
          recipe_name: string;
          listing_count: number;
          total_orders: number;
          total_revenue_cents: number;
          avg_orders_per_listing: number;
        }>;
        recent_activity: Array<{
          id: number;
          title: string;
          niche_keyword: string;
          orders: number;
          revenue_cents: number;
          last_order_at: string;
        }>;
      }>('/analytics/dashboard'),
    recipes: () =>
      fetchApi<{
        recipes: Array<{
          recipe_id: number;
          recipe_name: string;
          listing_count: number;
          total_orders: number;
          total_revenue_cents: number;
          avg_orders_per_listing: number;
          avg_revenue_per_listing: number;
        }>;
        summary: {
          total_recipes: number;
          total_orders: number;
          total_revenue_cents: number;
        };
      }>('/analytics/recipes'),
    niches: () =>
      fetchApi<{
        niches: Array<{
          niche_id: number;
          niche_keyword: string;
          product_type: string;
          recipe_id: number;
          recipe_name: string;
          listing_count: number;
          total_orders: number;
          total_revenue_cents: number;
          avg_orders_per_listing: number;
        }>;
        summary: {
          total_niches: number;
          total_orders: number;
          total_revenue_cents: number;
        };
      }>('/analytics/niches'),
    models: () =>
      fetchApi<{
        models: Array<{
          model_id: string;
          provider: string;
          model_type: string;
          listing_count: number;
          total_orders: number;
          total_revenue_cents: number;
          avg_orders_per_listing: number;
        }>;
        summary: {
          total_models: number;
          total_orders: number;
          total_revenue_cents: number;
        };
      }>('/analytics/models'),
    listings: () =>
      fetchApi<{
        listings: Array<{
          id: number;
          title: string;
          niche_id: number;
          niche_keyword: string;
          recipe_id: number;
          recipe_name: string;
          product_type: string;
          text_model_id: string;
          image_model_id: string;
          printify_product_id: string;
          created_at: string;
          orders: number;
          revenue_cents: number;
          last_order_at: string;
        }>;
        summary: {
          listings_with_orders: number;
          total_orders: number;
          total_revenue_cents: number;
        };
      }>('/analytics/listings'),
    sync: () =>
      fetchApi<{
        success: boolean;
        orders_processed: number;
        products_found: number;
        listings_updated: number;
        synced_at: string;
      }>('/analytics/sync', { method: 'POST' }),
  },

  // App Settings
  settings: {
    get: () =>
      fetchApi<{
        settings: {
          default_text_model: string;
          default_image_model: string;
          default_research_model: string;
        };
      }>('/settings'),
    update: (data: {
      default_text_model?: string;
      default_image_model?: string;
      default_research_model?: string;
    }) =>
      fetchApi<{
        success: boolean;
        settings: {
          default_text_model: string;
          default_image_model: string;
          default_research_model: string;
        };
      }>('/settings', { method: 'PUT', body: JSON.stringify(data) }),
  },

  // Notifications
  notifications: {
    list: () =>
      fetchApi<{
        notifications: Array<{
          id: number;
          type: 'niche_discovered' | 'system' | 'alert';
          title: string;
          message: string | null;
          metadata: string | null;
          read: number;
          created_at: string;
        }>;
        unread_count: number;
      }>('/notifications'),
    markAsRead: (id: number) =>
      fetchApi<{ success: boolean }>(`/notifications/${id}/read`, { method: 'PUT' }),
    markAllAsRead: () =>
      fetchApi<{ success: boolean }>('/notifications/read-all', { method: 'PUT' }),
  },

  // Trend Prompts
  trends: {
    // Get available niches
    niches: () => fetchApi<{ niches: string[] }>('/trends/niches'),

    // Generate prompt variations
    generate: (data: {
      original_prompt: string;
      niche: string;
      count: number;
      text_model?: string;
    }) =>
      fetchApi<{
        success: boolean;
        count: number;
        prompts: Array<{ id: string; prompt: string; reasoning?: string }>;
        total_cost_usd: number;
      }>('/trends/generate', { method: 'POST', body: JSON.stringify(data) }),

    // List all trend prompts
    list: (params?: { status?: string; niche?: string; limit?: number }) => {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.niche) query.set('niche', params.niche);
      if (params?.limit) query.set('limit', String(params.limit));
      const qs = query.toString();
      return fetchApi<{
        prompts: Array<{
          id: string;
          original_prompt: string;
          niche: string;
          generated_prompt: string;
          status: string;
          image_url: string | null;
          listing_id: number | null;
          total_cost_usd: number;
          created_at: string;
        }>;
      }>(qs ? `/trends?${qs}` : '/trends');
    },

    // Get single prompt
    get: (id: string) =>
      fetchApi<{
        prompt: {
          id: string;
          original_prompt: string;
          niche: string;
          generated_prompt: string;
          status: string;
          image_key: string | null;
          image_url: string | null;
          listing_id: number | null;
          prompt_cost_usd: number;
          image_cost_usd: number;
          total_cost_usd: number;
          created_at: string;
          updated_at: string;
        };
      }>(`/trends/${id}`),

    // Update status (approve/reject)
    updateStatus: (id: string, status: 'approved' | 'rejected') =>
      fetchApi<{ success: boolean; id: string; status: string }>(`/trends/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),

    // Generate image from prompt
    generateImage: (
      id: string,
      data?: { image_model?: string; product_type?: 'wall_art' | 'apparel' }
    ) =>
      fetchApi<{ success: boolean; id: string; image_url: string; image_cost_usd: number }>(
        `/trends/${id}/generate-image`,
        { method: 'POST', body: JSON.stringify(data ?? {}) }
      ),

    // Delete prompt
    delete: (id: string) => fetchApi<{ success: boolean }>(`/trends/${id}`, { method: 'DELETE' }),

    // Bulk action
    bulkAction: (data: { ids: string[]; action: 'approve' | 'reject' | 'delete' }) =>
      fetchApi<{ success: boolean; action: string; affected: number }>('/trends/bulk-action', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Convert to listing
    convertToListing: (
      id: string,
      data?: { text_model?: string; product_type?: 'digital_download' | 'printify_pod' }
    ) =>
      fetchApi<{ success: boolean; listing_id: number; title: string; seo_cost_usd: number }>(
        `/trends/${id}/convert-to-listing`,
        { method: 'POST', body: JSON.stringify(data ?? {}) }
      ),
  },
};
